import { normalizeAreaIdForLevel } from "../idUtils.js";
import { buildLabelsAndColors } from "../colorScale.js";
import { DEMO_LABELS } from "../demographicLabels.js";
import { schemaTypeFor } from "../schemaTypes.js";

/**
 * Async pure orchestration core of MapPage.fetchTopDemographicInsight().
 *
 * Verbatim lift: all this.state/this.props reads → inputs fields; all
 * instance-field scaffolding (dedup key, AbortController, cache) → wrapper;
 * all dispatch (onAudienceInsightChange, loading state) → wrapper.
 *
 * Transport contract — `fetchRows(url, body, signal)` has THREE outcomes:
 *   returns rows[]   success (including valid-empty [])
 *   throws SkipError !res.ok — the `continue` skip logic stays HERE in the
 *                    core, exactly at the original `if (!res.ok) continue` site
 *   throws AbortError signal fired — propagates to wrapper catch block
 * A REPLAY_MISS (core requests something not in the replay map) is a test
 * failure indicating the request sequence diverged.
 *
 * ⚠  Punch-list (preserved verbatim, do not fix):
 *   - MIN_AUDIENCE_N = 200 is hardcoded (not a config prop)
 *   - media_market level with stateCode silently produces stateCodes=[]
 *   - weightedStats reads r.total_sum regardless of weightCol (correct: API
 *     normalises weighted sum to total_sum regardless of weight column chosen)
 *
 * ⚠  Cache-key change (result-irrelevant, documented):
 *   The wrapper cache adapter keys on JSON.stringify({url,body}) rather than
 *   the original compound string. This changes which requests hit the cache
 *   (performance) but cannot change results (cache hit == miss == same rows,
 *   proven in 1E/1I — cache is result-irrelevant). Logged here for audit trail.
 *
 * Returns the result object for the wrapper to dispatch, null for no-data,
 * or undefined for early-abort-check exits (matching original `return;` paths).
 *
 * @param {object}   inputs
 * @param {Function} inputs.fetchRows  async (url, body, signal) => rows[]
 * @param {AbortSignal} inputs.signal
 * @returns {Promise<object|null|undefined>}
 */
export async function computeAudienceInsight(inputs) {
  const {
    selectedStateCode: stateCode,
    selectedFeature,
    subVar,
    theme,
    group,
    weightCol,
    groupsById,
    theme_colours,
    fetchRows,
    signal,
  } = inputs;

  // ── Guard clauses (same as original — wrapper also checks these, belt+suspenders) ──
  if (inputs.ActionMode !== "filter") return;
  if (!group?.TargetVariable1?.keys?.length) return;
  if (!subVar) return;

  const level = selectedFeature?.level || "states";
  const selectedFeatureIds = new Set(
    (selectedFeature?.rowMatchIds || [selectedFeature?.value])
      .filter((id) => id != null)
      .map((id) => String(id))
  );

  const toArray = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
  const posKeys = toArray(group?.TargetVariable1?.polarity?.pos);
  const negKeys = toArray(group?.TargetVariable1?.polarity?.neg);
  const groupKeys =
    subVar === "net"
      ? [...posKeys, ...negKeys]
      : [subVar];

  const baseBody = {
    stateCodes:
      stateCode && level !== "states" && level !== "media_market"
        ? [stateCode]
        : stateCode && level === "states"
          ? [stateCode]
          : [],
    theme,
    groupKeys,
    schema: (group?.TargetVariable1?.schema || group?.schema || "public"),
    schema_type: schemaTypeFor(group?.TargetVariable1?.schema || group?.schema || "public"),
    weightCol,
    aggregation_column: (group?.TargetVariable1?.aggregation_column || group?.aggregation_column || "total"),
    filters: {},
  };

  const sumKeys = (keys, row) =>
    keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);

  const responseValue = (row) => {
    if (subVar === "net") {
      const pos = sumKeys(posKeys, row);
      const neg = sumKeys(negKeys, row);
      return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
    }
    const v = Number(row?.[subVar]);
    return Number.isFinite(v) ? v : null;
  };

  const MIN_AUDIENCE_N = 200; // ⚠ hardcoded, punch-list item

  // Minimum share of an AREA's base that a demographic sub-group must hold for
  // that area to count toward its score. Ported from the Constituency app's
  // fetchAudienceInsight. Without it, thin slices — "Other" 2024 voters, or a
  // small ethnic group that is a few per cent almost everywhere — win the sweep
  // off a handful of respondents, because a tiny base produces a wild average.
  //
  // This complements MIN_AUDIENCE_N rather than replacing it: that one is an
  // absolute floor on the summed base across the whole scope, this one is a
  // relative floor applied per area BEFORE the averaging.
  const MIN_SUBGROUP_SHARE_PCT = 10;

  const areaKeyOf = (r) => String(r?.area_id ?? "");

  // baseByArea maps area_id -> that area's UNFILTERED weighted base, so a
  // sub-group's share of an area is its own total_sum over that total. Passing
  // a null/empty map disables the floor (see the baseline fetch below).
  const weightedStats = (rows, baseByArea) => {
    const normalizeScopeId = (id) => normalizeAreaIdForLevel(level, id);
    const normalizedSelectedIds = new Set(
      Array.from(selectedFeatureIds).map((id) => normalizeScopeId(id))
    );
    const selectedScope = Boolean(selectedFeature && normalizedSelectedIds.size);
    const sc = stateCode ? String(stateCode).padStart(2, "0") : null;
    const useShareFloor = Boolean(baseByArea && baseByArea.size > 0);
    let num = 0;
    let den = 0;
    let validCount = 0;
    let inScopeWithData = 0;
    let shareNum = 0;
    let shareDen = 0;
    for (const r of rows || []) {
      if (selectedScope) {
        const rowId = String(r?.area_id ?? "");
        const normalizedRowId = normalizeScopeId(rowId);
        if (!normalizedSelectedIds.has(normalizedRowId) && !selectedFeatureIds.has(rowId)) continue;
      } else if (sc) {
        const id = String(r?.area_id ?? "");
        if (!id.startsWith(sc)) continue;
      }
      const val = responseValue(r);
      const w = Number(r?.total_sum); // ⚠ hardcoded to total_sum, punch-list item
      if (!(Number.isFinite(val) && Number.isFinite(w) && w > 0)) continue;
      inScopeWithData += 1;
      // Share floor: drop areas where this sub-group is too small a slice.
      // Areas missing from the baseline are kept rather than dropped, so a
      // partial baseline can never silently delete a whole candidate.
      if (useShareFloor) {
        const areaTotal = baseByArea.get(areaKeyOf(r));
        if (Number.isFinite(areaTotal) && areaTotal > 0) {
          shareNum += w;
          shareDen += areaTotal;
          if ((w / areaTotal) * 100 < MIN_SUBGROUP_SHARE_PCT) continue;
        }
      }
      num += val * w;
      den += w;
      validCount += 1;
    }
    if (!validCount || !den) {
      return {
        avg: null,
        total: den,
        // Had data somewhere in scope, but nowhere big enough to count. This is
        // what distinguishes "excluded for being small" from "no data at all".
        belowFloor: useShareFloor && inScopeWithData > 0,
        sharePct: shareDen > 0 ? (shareNum / shareDen) * 100 : null,
      };
    }
    return {
      avg: num / den,
      total: den,
      belowFloor: false,
      sharePct: shareDen > 0 ? (shareNum / shareDen) * 100 : null,
    };
  };

  const candidates = {
    gender: [1, 2],
    age: [1, 2, 3],
    education: [1, 2],
    ethnicity: [1, 2, 3, 4, 5],
    vote24: [1, 2, 3, 4],
  };

  try {
    // Unfiltered baseline for the same scope: the denominator for each
    // sub-group's per-area share. One extra request for the whole sweep. If it
    // fails we score WITHOUT the floor rather than excluding everything, so a
    // transport hiccup degrades to the old behaviour instead of emptying the
    // result.
    const baseByArea = new Map();
    try {
      const baselineRows = await fetchRows(
        `/api/${level}_data_arrow/`,
        { ...baseBody, filters: {} },
        signal
      );
      for (const r of baselineRows || []) {
        const k = areaKeyOf(r);
        const n = Number(r?.total_sum);
        if (k && Number.isFinite(n)) baseByArea.set(k, (baseByArea.get(k) || 0) + n);
      }
    } catch (e) {
      if (e.name === "AbortError") throw e;
      // SkipError or anything else: leave baseByArea empty -> floor disabled.
    }
    const shareFloorActive = baseByArea.size > 0;

    const bestByDim = {};
    const excludedSmall = [];
    for (const [dim, values] of Object.entries(candidates)) {
      let bestDim = null;
      for (const val of values) {
        if (signal?.aborted) return; // abort-check: return undefined (loading stays, matching original)
        const body = { ...baseBody, filters: { [dim]: val } };
        let rows;
        try {
          // Transport contract: returns rows | throws SkipError (!res.ok) | throws AbortError
          rows = await fetchRows(`/api/${level}_data_arrow/`, body, signal);
        } catch (e) {
          if (e.name === "SkipError") continue; // !res.ok — skip this candidate, verbatim placement
          throw e; // AbortError or other — propagate to outer try/catch
        }
        const { avg, total, belowFloor, sharePct } = weightedStats(rows, baseByArea);
        // Excluded for being too small everywhere: record it so the UI can say
        // so, rather than the group just vanishing with no explanation.
        if (belowFloor) {
          excludedSmall.push({ dim, val, sharePct });
          continue;
        }
        if (!Number.isFinite(avg) || total < MIN_AUDIENCE_N) continue;
        if (!bestDim || avg > bestDim.score) {
          bestDim = { dim, val, score: avg };
        }
      }
      if (bestDim) bestByDim[dim] = bestDim;
    }

    let best = null;
    const topPairs = [];
    const dimsWithBest = Object.entries(bestByDim)
      .sort((a, b) => (b[1]?.score ?? -Infinity) - (a[1]?.score ?? -Infinity))
      .map(([dim]) => dim);
    const topDims = dimsWithBest.slice(0, 3);
    for (let i = 0; i < topDims.length; i += 1) {
      for (let j = i + 1; j < topDims.length; j += 1) {
        if (signal?.aborted) return; // abort-check
        const dimA = topDims[i];
        const dimB = topDims[j];
        const valA = bestByDim[dimA]?.val;
        const valB = bestByDim[dimB]?.val;
        if (valA == null || valB == null) continue;
        const body = { ...baseBody, filters: { [dimA]: valA, [dimB]: valB } };
        let rows;
        try {
          rows = await fetchRows(`/api/${level}_data_arrow/`, body, signal);
        } catch (e) {
          if (e.name === "SkipError") continue; // !res.ok — skip pair
          throw e;
        }
        // Pairs are scored against the same per-area floor. A pair is thinner
        // than either of its halves, so this is where it bites hardest, and
        // where an unfloored sweep produced the most spurious winners.
        const { avg, total } = weightedStats(rows, baseByArea);
        if (!Number.isFinite(avg) || total < MIN_AUDIENCE_N) continue;
        const candidate = { dimA, valA, dimB, valB, score: avg };
        if (!best || avg > best.score) {
          best = candidate;
        }
        topPairs.push(candidate);
      }
    }

    if (!best && Object.keys(bestByDim).length === 0) {
      return null; // no-data — wrapper fires loading=false, no result dispatch
    }

    // ── Result assembly — uses entry-time groupsById and theme_colours ──
    const labelFor = (dim, val) => {
      const map = DEMO_LABELS?.[dim] || {};
      return map[val] || String(val);
    };
    const dimLabel = (dim) => dim.charAt(0).toUpperCase() + dim.slice(1);
    const { labels } = buildLabelsAndColors(groupsById, theme_colours); // entry-time snapshots
    const responseLabel = subVar === "net" ? "Net" : (labels?.[subVar] || subVar);

    const topPairsSorted = topPairs
      .filter((p) => Number.isFinite(p.score))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((p) => ({
        label: `${labelFor(p.dimA, p.valA)} + ${labelFor(p.dimB, p.valB)}`,
        score: p.score,
      }));

    return {
      responseLabel,
      combined:
        best
          ? {
              dimension: `${dimLabel(best.dimA)} + ${dimLabel(best.dimB)}`,
              label: `${labelFor(best.dimA, best.valA)} + ${labelFor(best.dimB, best.valB)}`,
              score: best.score,
            }
          : null,
      topPairs: topPairsSorted,
      perDimension: Object.fromEntries(
        Object.entries(bestByDim).map(([dim, info]) => [
          dim,
          { label: labelFor(dim, info.val), score: info.score },
        ])
      ),
      summary: best
        ? `Strongest combined audience for ${responseLabel}: ${labelFor(best.dimA, best.valA)} + ${labelFor(best.dimB, best.valB)} (${best.score.toFixed(1)}%)`
        : null,
      // Share-floor reporting, so the UI can explain a missing group rather than
      // leaving the user to wonder why e.g. "Other" never appears. null when the
      // baseline fetch failed and the sweep ran unfloored.
      minSharePct: shareFloorActive ? MIN_SUBGROUP_SHARE_PCT : null,
      excludedSmall: excludedSmall.map((e) => labelFor(e.dim, e.val)),
    };
  } catch (e) {
    // Propagate — wrapper's try/catch handles abort vs other errors
    throw e;
  }
}
